// src/lib/api_marketing.ts
// Cliente API para el módulo de Marketing (Meta) — Django backend.
// Reutiliza el mismo proxy y auth JWT de Orion (auth_token)

// Absolute path — matches vite.config.ts proxy key. A relative value
// would resolve against the current location, which under react-router
// can be deep (`/m/x/y`) and break the fetch.
const DJANGO_PROXY = '/django-api';

import { ensureFreshToken } from '@/lib/tokenRefresh';

/* ─── Auth: reusa el JWT del login de Orion ─── */
async function getJwt(): Promise<string> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  if (!token) throw new Error('Sesión expirada. Inicia sesión nuevamente.');
  return token;
}

async function authHeaders(): Promise<Record<string, string>> {
  return {
    Authorization: `Bearer ${await getJwt()}`,
  };
}

/** Legacy no-op — token caching ahora vive en `ensureFreshToken`. Stubbed
 *  para no romper imports existentes. */
export function clearJwtCache(): void { /* no-op */ }

/** Custom error with HTTP status for granular UX handling */
export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function djangoFetch<T = any>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = await authHeaders();
  if (init?.body !== undefined && !(init?.headers && (init.headers as Record<string, string>)['Content-Type'])) { headers['Content-Type'] = 'application/json'; }
  const url = `${DJANGO_PROXY}${path}`;
  const res = await fetch(url, { ...init, headers: { ...headers, ...(init?.headers || {}) } });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = err.detail || `Django ${res.status}: ${res.statusText}`;
    if (res.status === 401) throw new ApiError('Sesión expirada. Inicia sesión nuevamente.', 401);
    if (res.status === 403) throw new ApiError(detail || 'Sin permisos para este módulo.', 403);
    if (res.status === 404) throw new ApiError(detail || 'Recurso no encontrado o no pertenece a tu organización.', 404);
    if (res.status === 422) throw new ApiError(detail || 'Error de validación. Revisa los campos.', 422);
    if (res.status === 502) throw new ApiError('Dependencia externa no disponible (Meta/Stripe). Intenta de nuevo.', 502);
    throw new ApiError(detail, res.status);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}


/* ═══════════════════════════════════════════════════════════════
   TYPES
   ═══════════════════════════════════════════════════════════════ */

export interface MarketingOverview {
  summary: { ad_accounts: number; pages: number; instagram_accounts: number; lead_forms: number };
  ad_accounts: AdAccount[];
  pages: PageInfo[];
  instagram_accounts: IgAccount[];
  lead_forms_by_page: Record<string, number>;
}

export interface AdAccount {
  id: string;
  name: string;
  account_status: number;
  currency: string;
  timezone_name?: string;
}

export interface PageInfo {
  id: string;
  name: string;
  category?: string;
  fan_count?: number;
  link?: string;
}

export interface IgAccount {
  id: string;
  username: string;
  name?: string;
  followers_count?: number;
  media_count?: number;
  profile_picture_url?: string;
}

export interface MarketingKpis {
  kpis: {
    ads_spend: number;
    ads_impressions: number;
    ads_clicks: number;
    ads_reach: number;
    ads_ctr_pct: number;
    pages_impressions: number;
    pages_engaged_users: number;
    instagram_impressions: number;
    instagram_reach: number;
    instagram_profile_views: number;
    instagram_follower_count: number;
  };
  inventory: { ad_accounts: number; pages: number; instagram_accounts: number };
  errors: Record<string, string>;
}

export interface LeadEntry {
  lead_id: string;
  created_time: string;
  form_id: string;
  form_name: string;
  ad_id: string | null;
  ad_name: string | null;
  fields: Record<string, string>;
  customer_name: string | null;
  customer_email: string | null;
  customer_phone: string | null;
}

export interface LeadsInbox {
  forms: { form_id: string; form_name: string; leads_count?: number; error?: string }[];
  total_leads: number;
  leads: LeadEntry[];
}

export interface PublishRequest {
  page_id: string;
  message: string;
  link?: string;
  publish_to_page: boolean;
  publish_to_instagram: boolean;
  ig_account_id?: string | null;
  ig_image_url?: string;
  ig_caption?: string;
}

export interface PublishResult {
  ok: boolean;
  results: Record<string, any>;
  errors: Record<string, string>;
}

/* ═══════════════════════════════════════════════════════════════
   ENDPOINTS
   ═══════════════════════════════════════════════════════════════ */

export function getMarketingOverview(includeLeads = true): Promise<MarketingOverview> {
  return djangoFetch(`/api/v1/admin/meta/marketing/overview?include_leads=${includeLeads}`);
}

export function getMarketingKpis(datePreset = 'last_7d'): Promise<MarketingKpis> {
  return djangoFetch(`/api/v1/admin/meta/marketing/dashboard-kpis?date_preset=${encodeURIComponent(datePreset)}`);
}

export function getLeadsInbox(params: {
  page_id: string; form_id?: string; limit_per_form?: number;
}): Promise<LeadsInbox> {
  const qs = new URLSearchParams();
  qs.set('page_id', params.page_id);
  if (params.form_id) qs.set('form_id', params.form_id);
  if (params.limit_per_form) qs.set('limit_per_form', String(params.limit_per_form));
  return djangoFetch(`/api/v1/admin/meta/marketing/leads-inbox?${qs}`);
}

export function publishUnified(body: PublishRequest): Promise<PublishResult> {
  return djangoFetch('/api/v1/admin/meta/marketing/publish-unified', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}
